import React, { PureComponent } from "react";
import moment from "moment";
import { connect } from "react-redux";
import { useAccordionButton, Button, Form, Row, Col } from "react-bootstrap";

import * as userActions from "../../../state/user";
import * as toastActions from "../../../state/toasts";
import { editUser } from "../../../services/turgoil-api";
import { ToastType } from "../../../common/Toasts";
import Loader from "../../../common/Loader";
import { validateEmail } from "../../../utils/regex";
import TooltipWrapper from "../../CreateOilRequestPage/sub/TooltipWrapper";
import Icons from "./Icons";

moment.locale("et");

function getDateTime(dateTimeString) {
  const now = moment();
  const demandDateTime = moment(dateTimeString);
  if (now.year() !== demandDateTime.year()) {
    return demandDateTime.format("DD.MM.YYYY HH:mm");
  }
  return demandDateTime.format("D. MMMM HH:mm");
}

function CustomAccordionToggle({ children }) {
  const decoratedOnClick = useAccordionButton("");

  return (
    <Button type="button" variant="link" onClick={decoratedOnClick}>
      {children}
    </Button>
  );
}

type Props = {
  user: any;
  setUser: (u: any) => void;
  showToast: typeof toastActions.showToast;
};

type User = {
  forename: string;
  surname: string;
  contactPhone: string;
  contactEmail: string;
};

type State = {
  loading: boolean;
  editing: boolean;
  errors: Record<string, string>;
  user: User;
};

class Profile extends PureComponent<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      loading: false,
      editing: false,
      errors: {},
      user: {
        forename: "",
        surname: "",
        contactPhone: "",
        contactEmail: "",
      },
    };
  }

  validate = () => {
    const { forename, surname, contactEmail, contactPhone } = this.state.user;
    const errors: Record<string, string> = {};

    if (!forename) {
      errors.forenameError = "Eesnimi on kohustuslik";
    }
    if (!surname) {
      errors.surnameError = "Perenimi on kohustuslik";
    }
    if (contactEmail.length && !validateEmail(contactEmail)) {
      errors.contactEmailError =
        "See ei ole korrektne e-posti aadress. Korrektne on näiteks info@turgoil.com";
    }
    if (contactPhone.length > 20) {
      errors.contactPhoneError =
        "Telefoni numbri maksimaalne pikkus on 20 tähemärki.";
    }

    this.setState({ errors });

    return Object.keys(errors).length === 0;
  };

  handleSubmit = (e) => {
    e.preventDefault();
    const { user, showToast } = this.props;

    if (!this.validate()) {
      return;
    }

    this.setState({ loading: true });
    editUser(user.id, this.state.user)
      .then((userRes) => {
        this.props.setUser(userRes);
        showToast({
          title: "Päring õnnestus",
          text: "Kasutaja andmete muutmine õnnestus.",
          type: ToastType.Success,
        });

        this.setState({
          loading: false,
          editing: false,
          user: {
            forename: "",
            surname: "",
            contactPhone: "",
            contactEmail: "",
          },
        });
      })
      .catch((err) => {
        showToast({
          title: "Päring ebaõnnestus.",
          text: err.message,
          type: ToastType.Error,
        });

        this.setState({
          loading: false,
          errors: {
            generalError: err.message,
          },
        });
      });
  };

  handleChange = (e) => {
    const { name, value } = e.target;

    this.setState((prevState) => ({
      user: {
        ...prevState.user,
        [name]: value,
      },
      errors: {
        ...prevState.errors,
        [`${name}Error`]: "",
        generalError: "",
      },
    }));
  };

  closeEditingView = () => {
    this.setState({ editing: false, errors: {} });
  };

  openEditingView = () => {
    const { user } = this.props;
    this.setState({
      editing: true,
      user: {
        contactEmail: user.contactEmail || "",
        contactPhone: user.contactPhone || "",
        forename: user.forename || "",
        surname: user.surname || "",
      },
    });
  };

  private getValue = (name: string): string | undefined => {
    const { editing } = this.state;
    const { user } = this.props;

    if (editing) {
      return this.state.user[name];
    }

    return user[name];
  };

  render() {
    const { errors, editing, loading } = this.state;

    if (loading) {
      return <Loader />;
    }

    return (
      <form onSubmit={this.handleSubmit}>
        <div className="settings__content-title">Andmed</div>

        <Form.Group className="mb-2" as={Row}>
          <Form.Label column sm={4} htmlFor="forename">
            Eesnimi
          </Form.Label>
          <Col sm={8}>
            <Form.Control
              id="forename"
              name="forename"
              className="form-control"
              disabled={!editing}
              plaintext={!editing}
              onChange={this.handleChange}
              value={this.getValue("forename")}
              isInvalid={!!errors.forenameError}
            />
            <Form.Control.Feedback type="invalid">
              {errors.forenameError}
            </Form.Control.Feedback>
          </Col>
        </Form.Group>
        <Form.Group className="mb-2" as={Row}>
          <Form.Label column sm={4} htmlFor="surname">
            Perenimi
          </Form.Label>
          <Col sm={8}>
            <Form.Control
              id="surname"
              name="surname"
              className="form-control"
              disabled={!editing}
              plaintext={!editing}
              onChange={this.handleChange}
              value={this.getValue("surname")}
              isInvalid={!!errors.surnameError}
            />
            <Form.Control.Feedback type="invalid">
              {errors.surnameError}
            </Form.Control.Feedback>
          </Col>
        </Form.Group>
        <Form.Group className="mb-2" as={Row}>
          <Form.Label column sm={4} htmlFor="email">
            E-posti aadress
          </Form.Label>
          <Col sm={8}>
            <Form.Control
              id="email"
              name="email"
              className="form-control"
              disabled
              plaintext
              onChange={this.handleChange}
              value={this.getValue("email")}
            />
          </Col>
        </Form.Group>
        <Form.Group className="mb-2" as={Row}>
          <Form.Label
            column
            sm={4}
            htmlFor="contactEmail"
            style={{ position: "relative" }}
          >
            Kontakt e-posti aadress&nbsp;
            <TooltipWrapper
              id="1"
              text="E-posti aadress, mis saadetakse hanke võitjale.
            Lisaks hakkab see olema e-posti aadress, kuhu saadetakse teavitusi."
              position="absolute"
            >
              {Icons.Info}
            </TooltipWrapper>
          </Form.Label>
          <Col sm={8}>
            <Form.Control
              id="contactEmail"
              name="contactEmail"
              className="form-control"
              disabled={!editing}
              plaintext={!editing}
              onChange={this.handleChange}
              value={this.getValue("contactEmail")}
              isInvalid={!!errors.contactEmailError}
            />
            <Form.Control.Feedback type="invalid">
              {errors.contactEmailError}
            </Form.Control.Feedback>
          </Col>
        </Form.Group>
        <Form.Group className="mb-2" as={Row}>
          <Form.Label
            column
            sm={4}
            htmlFor="contactPhone"
            style={{ position: "relative" }}
          >
            Kontakt telefoni number&nbsp;
            <TooltipWrapper
              id="2"
              text="Telefoni number, mis saadetakse hanke võitjale."
              position="absolute"
            >
              {Icons.Info}
            </TooltipWrapper>
          </Form.Label>
          <Col sm={8}>
            <Form.Control
              id="contactPhone"
              name="contactPhone"
              className="form-control"
              disabled={!editing}
              plaintext={!editing}
              onChange={this.handleChange}
              value={this.getValue("contactPhone")}
              isInvalid={!!errors.contactPhoneError}
            />
            <Form.Control.Feedback type="invalid">
              {errors.contactPhoneError}
            </Form.Control.Feedback>
          </Col>
        </Form.Group>

        {!editing && (
          <div className="settings__content-footer">
            <Button
              type="button"
              className="ms-auto"
              onClick={this.openEditingView}
            >
              Muuda
            </Button>
          </div>
        )}

        {editing && (
          <div className="settings__content-footer">
            <Button
              className="mr-3"
              variant="link"
              type="button"
              onClick={this.closeEditingView}
            >
              Tühista
            </Button>
            <Button
              className="float-end"
              onClick={this.handleSubmit}
              type="submit"
            >
              Salvesta
            </Button>
          </div>
        )}
      </form>
    );
  }
}

function mapStateToProps(state) {
  return {
    user: state.userReducer.user,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    setUser: (user) => {
      dispatch(userActions.success(user));
    },
    showToast: (toast) => dispatch(toastActions.showToast(toast)),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(Profile);
